import React, { useState, useRef } from 'react';
import { useExemplos } from '../utils/exemplosPessoais';
import {
  parseQuickReminder, parseQuickGoal, parseQuickInvestment,
  ParsedReminder, ParsedGoal, ParsedInvestment, createCategory,
} from '../services/api';
import { Category } from '../types/Category';
import InvestmentProposal, { investmentIncomplete } from './InvestmentProposal';
import Toast from './Toast';
import './QuickEntry.css';
/* O MESMO número que o schema do servidor usa — ver utils/limites.ts. */
import { LIMITES } from '../utils/limites';
import { formatBRL } from '../utils/format';
import { useTelaEstreita } from '../utils/telaEstreita';
import { useFechavelComVoltar } from '../contexts/VoltarDoCelular';
import { rotuloDoMes } from './SeletorDeMes';
import { ehTetoDeIa, AVISO_TETO_DE_IA } from '../utils/tetoDeIa';

type Modo = 'reminder' | 'goal' | 'investment';

interface Props {
  modo: Modo;
  /** Só usado em meta, para resolver teto de gasto por categoria. */
  categories?: Category[];
  onCreate: (data: any) => Promise<void>;
  onCategoryCreated?: () => void;
  /**
   * Abre/fecha o formulário completo. Omitido quando não existe formulário de
   * criação — em Metas, o campo único é o ÚNICO caminho para criar, e o
   * formulário só aparece para editar uma meta existente.
   */
  onToggleForm?: () => void;
  formAberto?: boolean;
}

const CONFIG = {
  reminder: {
    placeholder: 'todo dia 10 pagar aluguel 1200',
    /* 231px numa caixa de 228 com a fonte de 16. Sem o valor, sobra folga de
       38 e a gramática que ensina (dia + ação) continua inteira. */
    placeholderCurto: 'todo dia 10 pagar aluguel',
    exemplos: ['todo dia 10 pagar aluguel 1200', 'netflix dia 5', 'recebo salário dia 30'],
    vazio: 'Não identifiquei um lembrete. Diga o dia do mês.',
  },
  goal: {
    placeholder: 'quero juntar 20 mil pra viagem',
    /* 231 numa caixa de 232 era um pixel de folga: não dá para confiar
       entre aparelhos, porque a fonte varia. */
    placeholderCurto: 'quero juntar 20 mil',
    /*
      Os exemplos ENSINAM a gramática, então cada um traz uma peça nova.
      
      Eram três frases sem prazo, e o prazo passou a existir — exemplo que não
      mostra o que dá para dizer é o motivo mais comum de um recurso não ser
      usado. Cada um deles é extraído de verdade
      (`services/ai/entityPrompts.ts`, regra 6): o primeiro nomeia um mês, o
      segundo dá uma duração, o terceiro continua sem prazo, porque prazo é
      opcional e o exemplo não pode sugerir que é obrigatório.
    */
    exemplos: [
      'quero juntar 20 mil pra viagem até dezembro',
      'não quero gastar mais de 500 por mês com delivery pelos próximos 6 meses',
      'reserva de emergência de 30 mil, já tenho 5 mil',
    ],
    vazio: 'Não identifiquei uma meta. Diga o valor que quer atingir.',
  },
  investment: {
    placeholder: 'investi 5 mil em CDB no Nubank que rende 6% ao ano',
    /* Medido num Android de 360px: o longo pede 388px numa caixa de 224. Este
       pede 147. O exemplo completo continua no primeiro chip abaixo do campo,
       onde ele cabe porque o texto quebra em linhas. */
    placeholderCurto: 'investi 5 mil em CDB',
    exemplos: [
      'investi 5 mil em CDB no Nubank que rende 6% ao ano',
      'aportei 500 no tesouro',
      'tenho 3 mil no CDB do Inter a 0,9% ao mês',
    ],
    vazio: 'Não identifiquei uma aplicação. Diga o valor aplicado.',
  },
} as const;

const PALETA = ['#6d28d9', '#db2777', '#dc2626', '#ea580c', '#059669', '#2563eb'];

/**
 * Campo único para lembretes, metas e investimentos — mesma ideia do lançamento.
 *
 * Diferença de desenho: aqui **não há histórico para completar lacunas**. Um
 * lembrete ou uma meta são declarações, não eventos repetidos, então não existe
 * "qual dia você costuma usar". O que faltar vira um campo que ela preenche
 * ali mesmo, com o resto já pronto.
 *
 * O card de investimento mora em `InvestmentProposal` porque o campo de
 * lançamento também chega nele — "investi 5 mil em CDB" escrito no campo de
 * gasto cai na mesma confirmação.
 */
const EntityQuickEntry: React.FC<Props> = ({
  modo, categories = [], onCreate, onCategoryCreated, onToggleForm, formAberto = false,
}) => {
  const estreita = useTelaEstreita();

  /* O formulário que este campo abre entra na pilha do voltar, como os
     outros. Quem fecha é o mesmo `onToggleForm` do botão. */
  useFechavelComVoltar(formAberto, React.useCallback(() => onToggleForm?.(), [onToggleForm]));
  const cfg = CONFIG[modo];
  /*
   * Os chips de META usam as categorias dela quando há dado. Lembrete e
   * investimento continuam genéricos: os exemplos deles ensinam formatos (dia
   * do mês, taxa) que nenhum nome dela torna mais claro.
   */
  const pessoais = useExemplos();
  const exemplos: readonly string[] = modo === 'goal' ? pessoais.meta : cfg.exemplos;
  const [texto, setTexto] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [feito, setFeito] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [rem, setRem] = useState<ParsedReminder | null>(null);
  const [goal, setGoal] = useState<ParsedGoal | null>(null);
  const [inv, setInv] = useState<ParsedInvestment | null>(null);
  /** Em qual período ela quer digitar a taxa que faltou. */
  const [basePeriodo, setBasePeriodo] = useState<'monthly' | 'annual'>('annual');
  const [criandoCat, setCriandoCat] = useState(false);
  const [nomeCat, setNomeCat] = useState('');
  const [extras, setExtras] = useState<{ id: string; name: string }[]>([]);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  /* Confirmação do que ela acabou de escrever: não obedece ao olhinho.
     Ver a nota longa em `DebtProposal.tsx`. */
  const fmt = (v: number) => formatBRL(v);

  const limpar = () => { setRem(null); setGoal(null); setInv(null); setFeito(null); setErro(null); };

  const interpretar = async () => {
    if (!texto.trim() || carregando) return;
    setCarregando(true);
    limpar();
    try {
      if (modo === 'reminder') {
        const r = await parseQuickReminder(texto.trim());
        if (!r.reminder) { setErro(r.problem ?? cfg.vazio); return; }
        setRem(r.reminder);
      } else if (modo === 'investment') {
        const r = await parseQuickInvestment(texto.trim());
        if (!r.investment) { setErro(r.problem ?? cfg.vazio); return; }
        setInv(r.investment);
        setBasePeriodo('annual');
      } else {
        const r = await parseQuickGoal(texto.trim());
        if (!r.goal) { setErro(r.problem ?? cfg.vazio); return; }
        setGoal(r.goal);
      }
    } catch (e: any) {
      /*
       * Teto de IA: abre o formulário em vez de virar beco sem saída.
       *
       * `onToggleForm` continua opcional, e a condição continua necessária:
       * **Metas passa a ligá-lo em 04/09, Lembretes e Investimentos ainda
       * não.** Neles o formulário só existe para editar algo que já existe,
       * então a mensagem do servidor continua inteira, COM o tempo — quando
       * não há alternativa, "volte em 50 min" é a única coisa acionável.
       *
       * Prometer um formulário que não vai aparecer seria pior que a mensagem
       * que este conserto veio substituir.
       */
      if (ehTetoDeIa(e) && onToggleForm) {
        if (!formAberto) onToggleForm();
        setErro(AVISO_TETO_DE_IA);
        return;
      }
      setErro(e?.response?.data?.error ?? 'Não consegui interpretar agora.');
    } finally {
      setCarregando(false);
    }
  };

  const criarCategoria = async () => {
    const nome = nomeCat.trim();
    if (!nome || !goal) return;
    try {
      const c = await createCategory({ name: nome, color: PALETA[Math.floor(Math.random() * PALETA.length)] });
      setExtras(p => [...p, { id: c.id, name: c.name }]);
      setGoal({ ...goal, categoryId: c.id, categoryName: c.name, missing: goal.missing.filter(m => m !== 'categoryId') });
      setCriandoCat(false);
      setNomeCat('');
      onCategoryCreated?.();
    } catch {
      setErro(`Não consegui criar "${nome}".`);
    }
  };

  const faltaAlgo =
    modo === 'reminder' ? (!rem || rem.dayOfMonth == null)
    // Taxa NÃO entra: aplicação sem rendimento cadastrado é estado legítimo —
    // o que ela perde é a projeção, e o card diz isso em vez de bloquear.
    : modo === 'investment' ? investmentIncomplete(inv)
    : (!goal || goal.targetAmount == null || (goal.type === 'category_limit' && !goal.categoryId));

  const gravar = async () => {
    if (salvando || faltaAlgo) return;
    setSalvando(true);
    try {
      if (modo === 'reminder' && rem) {
        await onCreate({
          description: rem.description,
          type: rem.type,
          value: rem.value,
          dayOfMonth: rem.dayOfMonth,
        });
        setFeito(`${rem.description} · dia ${rem.dayOfMonth}`);
      } else if (modo === 'investment' && inv) {
        await onCreate(inv);
        setFeito(
          inv.operation === 'contribute'
            ? `aporte de ${fmt(inv.amount!)} em ${inv.name}`
            : `${inv.name} · ${fmt(inv.amount!)}`,
        );
      } else if (goal) {
        await onCreate({
          type: goal.type,
          name: goal.name,
          targetAmount: goal.targetAmount,
          savedAmount: goal.savedAmount,
          categoryId: goal.categoryId,
          deadlineMonth: goal.deadlineMonth,
          color: PALETA[Math.floor(Math.random() * PALETA.length)],
        });
        // O prazo entra na confirmação: ele veio de uma leitura da frase dela,
        // e leitura que não é mostrada é leitura que ninguém corrige.
        setFeito(
          `${goal.name} · ${fmt(goal.targetAmount!)}`
          + (goal.deadlineMonth ? ` · até ${rotuloDoMes(goal.deadlineMonth)}` : ''),
        );
      }
      setRem(null); setGoal(null); setInv(null); setTexto('');
      inputRef.current?.focus();
    } catch {
      setErro('Não consegui salvar. Tente de novo.');
    } finally {
      setSalvando(false);
    }
  };

  const temProposta = !!rem || !!goal || !!inv;
  const opcoesCat = [...categories.map(c => ({ id: c.id, name: c.name })), ...extras];

  return (
    <div className="qe">
      <Toast message={feito && `Criado: ${feito}`} onDismiss={() => setFeito(null)} />

      <div className="qe-input-row">
        <textarea
        maxLength={LIMITES.textoIa}
          ref={inputRef}
          className="qe-input"
          rows={1}
          placeholder={estreita ? cfg.placeholderCurto : cfg.placeholder}
          value={texto}
          onChange={e => {
            setTexto(e.target.value);
            limpar();
            // Voltar a digitar significa que ela desistiu do formulário.
            if (formAberto) onToggleForm?.();
          }}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); interpretar(); } }}
          disabled={carregando || salvando}
        />
        <button className="qe-go" onClick={interpretar} disabled={!texto.trim() || carregando || salvando}>
          {carregando ? <span className="qe-spin" /> : '→'}
        </button>
      </div>

      {!temProposta && !erro && (
        <div className="qe-hints">
          {exemplos.map(ex => (
            <button key={ex} className="qe-hint" onClick={() => { setTexto(ex); inputRef.current?.focus(); }}>
              {ex}
            </button>
          ))}
        </div>
      )}

      {erro && <div className="qe-erro">{erro}</div>}

      {!temProposta && onToggleForm && (
        <button type="button" className="qe-manual-fora" onClick={onToggleForm}>
          {formAberto ? 'Fechar formulário' : 'Preencher manualmente'}
        </button>
      )}

      {rem && (
        <div className="qe-card">
          <div className="qe-linha">
            <span className="qe-desc">{rem.description}</span>
            <span className={`qe-valor ${rem.type === 'income' ? 'income' : ''}`}>
              {rem.value != null ? `${rem.type === 'income' ? '+' : ''}${fmt(rem.value)}` : <em>sem valor fixo</em>}
            </span>
          </div>

          <div className="qe-gap">
            <div className="qe-gap-pergunta">
              {rem.dayOfMonth == null ? 'Que dia do mês?' : 'Todo dia do mês'}
            </div>
            <div className="qe-opcoes">
              <input
                className="qe-criar-input"
                style={{ width: 70 }}
                type="number"
                min="1"
                max="31"
                placeholder="dia"
                value={rem.dayOfMonth ?? ''}
                onChange={e => {
                  const d = parseInt(e.target.value);
                  setRem({ ...rem, dayOfMonth: Number.isFinite(d) && d >= 1 && d <= 31 ? d : null });
                }}
              />
              <button
                className={`qe-opcao${rem.type === 'expense' ? ' sugerida' : ''}`}
                onClick={() => setRem({ ...rem, type: 'expense' })}
              >a pagar</button>
              <button
                className={`qe-opcao${rem.type === 'income' ? ' sugerida' : ''}`}
                onClick={() => setRem({ ...rem, type: 'income' })}
              >a receber</button>
            </div>
          </div>

          <div className="qe-acoes">
            <button className="qe-salvar" onClick={gravar} disabled={faltaAlgo || salvando} aria-busy={salvando}>
              {faltaAlgo ? 'Informe o dia' : 'Criar lembrete'}
            </button>
            <button className="qe-cancelar" onClick={limpar} disabled={salvando}>Cancelar</button>
          </div>
        </div>
      )}

      {goal && (
        <div className="qe-card">
          <div className="qe-linha">
            <span className="qe-desc">{goal.name}</span>
            <span className="qe-valor">
              {goal.targetAmount != null ? fmt(goal.targetAmount) : <em>sem valor</em>}
            </span>
          </div>

          <div className="qe-chips">
            <span className="qe-chip">
              {goal.type === 'reserve' ? 'Reserva' : 'Teto de gasto'}
            </span>
            {goal.savedAmount > 0 && <span className="qe-chip">já tem {fmt(goal.savedAmount)}</span>}
            {goal.categoryName && <span className="qe-chip">{goal.categoryName}</span>}
          </div>

          {goal.targetAmount == null && (
            <div className="qe-gap">
              <div className="qe-gap-pergunta">Qual o valor da meta?</div>
              <div className="qe-opcoes">
                <input
                  className="qe-criar-input"
                  style={{ width: 130 }}
                  type="number"
                  placeholder="valor total"
                  onChange={e => {
                    const v = parseFloat(e.target.value);
                    setGoal({ ...goal, targetAmount: Number.isFinite(v) && v > 0 ? v : null });
                  }}
                />
              </div>
            </div>
          )}

          {/* Teto de gasto precisa de categoria. Se a que ela citou não existe,
              cria aqui — mandar sair da tela seria o atrito de volta. */}
          {goal.type === 'category_limit' && !goal.categoryId && (
            <div className="qe-gap">
              <div className="qe-gap-pergunta">Teto de gasto em qual categoria?</div>
              <div className="qe-opcoes">
                {criandoCat ? (
                  <div className="qe-criar">
                    <input
                      className="qe-criar-input"
                      autoFocus
                      placeholder="nome da categoria"
                      value={nomeCat}
                      onChange={e => setNomeCat(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { e.preventDefault(); criarCategoria(); }
                        if (e.key === 'Escape') { setCriandoCat(false); setNomeCat(''); }
                      }}
                    />
                    <button className="qe-criar-ok" onClick={criarCategoria} disabled={!nomeCat.trim()}>criar</button>
                    <button className="qe-criar-x" onClick={() => { setCriandoCat(false); setNomeCat(''); }}>✕</button>
                  </div>
                ) : (
                  <select
                    className="qe-outro"
                    value=""
                    onChange={e => {
                      if (e.target.value === '__novo__') { setCriandoCat(true); setNomeCat(''); }
                      else if (e.target.value) {
                        const c = opcoesCat.find(x => x.id === e.target.value)!;
                        setGoal({ ...goal, categoryId: c.id, categoryName: c.name, missing: goal.missing.filter(m => m !== 'categoryId') });
                      }
                    }}
                  >
                    <option value="">escolher…</option>
                    {opcoesCat.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    <option value="__novo__">+ criar categoria…</option>
                  </select>
                )}
              </div>
            </div>
          )}

          <div className="qe-acoes">
            <button className="qe-salvar" onClick={gravar} disabled={faltaAlgo || salvando} aria-busy={salvando}>
              {faltaAlgo ? 'Complete acima' : 'Criar meta'}
            </button>
            <button className="qe-cancelar" onClick={limpar} disabled={salvando}>Cancelar</button>
          </div>
        </div>
      )}

      {inv && (
        <InvestmentProposal
          inv={inv}
          onChange={setInv}
          onSave={gravar}
          onCancel={limpar}
          salvando={salvando}
          basePeriodo={basePeriodo}
          onBasePeriodo={setBasePeriodo}
        />
      )}

    </div>
  );
};

export default EntityQuickEntry;
